import React, { useState, useEffect } from 'react'
import { Box, Flex, Group, Menu, Button, Card, Grid, Text, Loader, Switch, Badge, Skeleton, Paper, Modal } from '@mantine/core'
import { IconPlus, IconDots } from '@tabler/icons-react'
import { getOrderDocument } from '../../../../services/orders'
import { useSelector, useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import { updateOrder, deleteOrderDocument } from '../../../../services/orders'
import { getOrderDetail } from '../../../../store/orderDetail'
import { notificationSuccess, notificationError, loadingNotification, completedLoadingNotification } from '../../../ui/Notifications'
import { calculation } from '../../../../services/calculation'
import NoData from '../../../ui/NoData'
import LoadingData from '../../../ui/LoadingData'
import OrderDocumentTable from './LayoutHelper/OrderDocumentTable'
import FormAddOrderDocument from './Form/FormAddOrderDocument'
import FormShareSalesOrder from './Form/FormShareSalesOrder'
import FormAdjustOrderNote from './Form/FormAdjustOrderNote'
import { modalDeleteData } from '../../../ui/Prompt/modalDeleteData'

const labelTable = [
  {
    label: 'Name',
    width: 'auto'
  },
  {
    label: 'Label',
    width: 150
  },
  {
    label: 'Attachment',
    width: 200
  },
  {
    label: 'Created Date',
    width: 200
  },
  {
    label: 'Action',
    width: 50
  },
]

const OrderDocuments = () => {
  const params = useParams()
  const orderId = params.order_id
  const dispatch = useDispatch()
  const { isLoadingOrder, summary } = useSelector(state => state.order_detail)
  const [loadingDocument, setLoadingDocument] = useState(true)
  const [documentList, setDocumentList] = useState([])
  const [showSalesQuote, setShowSalesQuote] = useState(false)
  const [loadingSalesQuote, setLoadingSalesQuote] = useState(false)
  const [openModalUpload, setOpenModalUpload] = useState(false)
  const [openModalShareOrder, setOpenModalShareOrder] = useState(false)
  const [openModalNoteOrder, setOpenModalNoteOrder] = useState(false)

  const handleGetDocumentsOrder = async (orderId) => {
    setLoadingDocument(true)
    try {
      const response = await getOrderDocument(orderId)
      if (response.success) {
        setDocumentList(response.data)
      } else {
        setDocumentList([])
      }
    } catch (error) {
      console.log(error)
    } finally {
      setLoadingDocument(false)
    }
  }

  const handleChangeSalesQuote = async (value) => {
    setLoadingSalesQuote(true)
    const payload = {
      is_show_sales_quote: value
    }
    try {
      const response = await updateOrder(orderId, payload)
      if (response.success) {
        setShowSalesQuote(value)
        notificationSuccess('Update Order Successfully', 'This data has been updated')
        const loadingCalculation = loadingNotification('Waiting Calculation Order', 'Please Wait. . .')
        const responseCalculation = await calculation(orderId)
        if (responseCalculation.success) {
          dispatch(getOrderDetail(orderId))
          completedLoadingNotification(loadingCalculation, 'Calculation Order Successfully', 'This order has been completed in calculation')
        }
      }
    } catch (error) {
      console.log(error)
      const errorMessage = error.response.data.message
      notificationError('Update Order Error', `${errorMessage ? errorMessage : 'Please recheck your form'}`)
    } finally {
      setLoadingSalesQuote(false)
    }
  }

  useEffect(() => {
    if (orderId) {
      handleGetDocumentsOrder(orderId)
    }
  }, [orderId])

  useEffect(() => {
    if (!isLoadingOrder) {
      setShowSalesQuote(summary.showSalesQuote)
    } 
  }, [isLoadingOrder, summary]) 

  const actions = {
    delete: (val) => {
      modalDeleteData('Document', val.attributes.id, val.attributes.name, deleteOrderDocument, () => handleGetDocumentsOrder(orderId))
    },
  }

  const handleAction = (val, type) => {
    return actions[type](val)
  }

  const onCloseModalUpload = () => {
    setOpenModalUpload(false)
  }

  const onCloseModalShareOrder = () => {
    setOpenModalShareOrder(false)
  }

  const onCloseModalNoteOrder = () => {
    setOpenModalNoteOrder(false)
  }

  const mappingDataDocument = (listDocument) => {
    if (listDocument.length > 0) {
      return (
        <Box>
          <OrderDocumentTable label={labelTable} data={documentList} actionMethod={handleAction} />
        </Box>
      )
    } else {
      return (
        <Paper shadow="sm" withBorder radius='md'>
          <NoData />
        </Paper>
      )
    }
  }

  const mappingFormSalesQuote = () => {
    return (
      <Group align='center' gap='xs'>
        <Switch
          checked={showSalesQuote}
          size='xs'
          onChange={(event) => handleChangeSalesQuote(event.currentTarget.checked)}
        />
        <Badge variant="light" color={showSalesQuote ? 'teal' : 'red'} tt='capitalize' fw='500'>{showSalesQuote ? 'Enabled' : 'Disabled'}</Badge>
      </Group>
    )
  }

  return (
    <Box>
      <Box my={20}>
        <Box>
          <Flex justify='flex-end' align='center' my={20}>
            <Group align='center'>
              <Button size='xs' radius='sm' leftSection={<IconPlus size={14} />} onClick={() => setOpenModalUpload(true)}>Upload Document</Button>
              <Menu width={180} position="bottom-end" withArrow arrowPosition="center">
                <Menu.Target>
                  <Button size="xs" radius='sm' variant='outline'><IconDots size={16} /></Button>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    fw='600'
                    fz={11}
                    onClick={() => setOpenModalShareOrder(true)}
                  >
                    Show Sales Order URL
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Group>
          </Flex>
        </Box>
        <Box my={30}>
          {loadingDocument ? <LoadingData /> : mappingDataDocument(documentList) }
        </Box>
        <Box>
          <Grid align='start' overflow='hidden'>
            <Grid.Col span={6}>
              <Card withBorder shadow="xs" radius="md" padding='md' mb='lg'>
                <Flex justify='space-between' align='center'>
                  <Text fz={12} fw='600'>Show Default Sales Quote</Text>
                  {!loadingSalesQuote ? mappingFormSalesQuote() : <Loader size='xs' />}
                </Flex>
              </Card>
              <Card withBorder shadow="xs" radius="md" padding='md' mb='lg'>
                <Card.Section withBorder inheritPadding py="xs">
                  <Flex justify='space-between' align='center'>
                    <Text fz={12} fw={600}>Sales Order Note</Text>
                    <Button size='compact-xs' variant="outline" color='black' onClick={() => setOpenModalNoteOrder(true)}>Edit</Button>
                  </Flex>
                </Card.Section>
                <Card.Section withBorder inheritPadding py='xs'>
                  <Box my={10}>
                    <Text fz={12} fw={500}>{!isLoadingOrder ? summary.salesNote || '-' : <Skeleton width={100} height={16} radius="xl" />}</Text>
                  </Box>
                </Card.Section>
              </Card>
            </Grid.Col>
          </Grid>
        </Box>
      </Box>
      <Modal opened={openModalUpload} onClose={onCloseModalUpload} centered closeOnClickOutside={false} size='xl' title={<Text fw='Bold'>Add New Document</Text>} withCloseButton={false}>
        <FormAddOrderDocument onCloseModal={onCloseModalUpload} reloadList={() => handleGetDocumentsOrder(orderId)} />
      </Modal>
      <Modal opened={openModalShareOrder} onClose={onCloseModalShareOrder} centered closeOnClickOutside={false} size='xl' title={<Text fw='Bold'>Share Sales Order / Quote PDF</Text>} withCloseButton={false}>
        <FormShareSalesOrder idOrder={orderId} onCloseModal={onCloseModalShareOrder} />
      </Modal>
      <Modal opened={openModalNoteOrder} onClose={onCloseModalNoteOrder} centered closeOnClickOutside={false} size='xl' title={<Text fw='Bold'>Update Sales Order Note</Text>} withCloseButton={false}>
        <FormAdjustOrderNote idOrder={orderId} onCloseModal={onCloseModalNoteOrder} />
      </Modal>
    </Box>
    
  )
}

export default OrderDocuments